import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ArrowLeft, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics";
import { authAPI } from "@/lib/api/auth";
import Version from "@/components/Version";

// The approval invite links here too, with a password *setup* token that the
// backend prefixes with "setup." (backend#904); a forgot-password link carries
// a plain reset token. Only the derived source is reported — never the token.
const PASSWORD_SETUP_TOKEN_PREFIX = "setup.";

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") ?? "";

  const [fields, setFields] = useState({ newPassword: "", confirmPassword: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [tokenError, setTokenError] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFields(prev => ({ ...prev, [name]: value }));
    setValidationError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (fields.newPassword !== fields.confirmPassword) {
      setValidationError("Passwords do not match.");
      return;
    }

    if (fields.newPassword.length < 8) {
      setValidationError("Password must be at least 8 characters.");
      return;
    }

    setIsLoading(true);

    try {
      await authAPI.resetPassword({ token, new_password: fields.newPassword });
      trackEvent("password_set", {
        source: token.startsWith(PASSWORD_SETUP_TOKEN_PREFIX) ? "invite" : "reset",
      });
      toast.success("Password updated! You can now sign in.");
      navigate("/login");
    } catch {
      setTokenError(true);
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url(/bondit_collegues_table.jpeg)" }}
        >
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        <div className="relative z-10 min-h-screen flex flex-col">
          <div className="bg-white/10 backdrop-blur-sm border-b border-white/20">
            <div className="container mx-auto px-4 py-4">
              <img
                src="/bondit-logo-trans-bg.png"
                alt="BondIT Logo"
                className="h-12 w-auto filter drop-shadow-lg"
              />
            </div>
          </div>
          <div className="flex-1 container mx-auto px-4 py-12">
            <div className="w-full max-w-md mx-auto">
              <Card className="shadow-2xl bg-white">
                <CardHeader className="text-center pb-6 bg-white">
                  <div className="flex justify-center mb-4">
                    <div className="rounded-full bg-red-100 p-3">
                      <AlertCircle className="h-6 w-6 text-red-600" />
                    </div>
                  </div>
                  <CardTitle className="text-2xl text-gray-900">Invalid link</CardTitle>
                  <CardDescription className="text-gray-600">
                    This password reset link is missing or invalid.
                  </CardDescription>
                </CardHeader>
                <CardFooter className="bg-white">
                  <Button asChild className="w-full">
                    <Link to="/forgot-password">Request a new link</Link>
                  </Button>
                </CardFooter>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: "url(/bondit_collegues_table.jpeg)",
        }}
      >
        <div className="absolute inset-0 bg-black/50"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header */}
        <div className="bg-white/10 backdrop-blur-sm border-b border-white/20">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex flex-col items-center">
                <img
                  src="/bondit-logo-trans-bg.png"
                  alt="BondIT Logo"
                  className="h-12 w-auto filter drop-shadow-lg mb-2"
                />
                <p className="text-sm text-white/90 drop-shadow text-center">
                  Choose a new password
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 container mx-auto px-4 py-12">
          <div className="w-full max-w-md mx-auto">
            <Card className="shadow-2xl backdrop-blur-sm bg-white border-white/20 dark:bg-white dark:border-white/20">
              {tokenError ? (
                <>
                  <CardHeader className="text-center pb-6 bg-white dark:bg-white">
                    <div className="flex justify-center mb-4">
                      <div className="rounded-full bg-red-100 p-3">
                        <AlertCircle className="h-6 w-6 text-red-600" />
                      </div>
                    </div>
                    <CardTitle className="text-2xl text-gray-900 dark:text-gray-900">
                      Link expired or invalid
                    </CardTitle>
                    <CardDescription className="text-gray-600 dark:text-gray-600">
                      This password reset link has expired or has already been used.
                    </CardDescription>
                  </CardHeader>
                  <CardFooter className="bg-white dark:bg-white flex-col gap-3">
                    <Button asChild className="w-full" size="lg">
                      <Link to="/forgot-password">Request a new link</Link>
                    </Button>
                    <Button asChild variant="ghost" className="w-full text-gray-600">
                      <Link to="/login">
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Back to sign in
                      </Link>
                    </Button>
                  </CardFooter>
                </>
              ) : (
                <form onSubmit={handleSubmit}>
                  <CardHeader className="text-center pb-6 bg-white dark:bg-white">
                    <CardTitle className="text-2xl text-gray-900 dark:text-gray-900">
                      Set new password
                    </CardTitle>
                    <CardDescription className="text-gray-600 dark:text-gray-600">
                      Enter and confirm your new password below.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4 bg-white dark:bg-white">
                    <div className="space-y-2">
                      <Label htmlFor="newPassword" className="text-gray-700 dark:text-gray-700">
                        New password
                      </Label>
                      <Input
                        id="newPassword"
                        name="newPassword"
                        type="password"
                        value={fields.newPassword}
                        onChange={handleChange}
                        placeholder="At least 8 characters"
                        required
                        autoComplete="new-password"
                        autoFocus
                        className="bg-white text-gray-900 border-gray-300 focus:border-blue-500 dark:bg-white dark:text-gray-900 dark:border-gray-300"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword" className="text-gray-700 dark:text-gray-700">
                        Confirm new password
                      </Label>
                      <Input
                        id="confirmPassword"
                        name="confirmPassword"
                        type="password"
                        value={fields.confirmPassword}
                        onChange={handleChange}
                        placeholder="Repeat your new password"
                        required
                        autoComplete="new-password"
                        className="bg-white text-gray-900 border-gray-300 focus:border-blue-500 dark:bg-white dark:text-gray-900 dark:border-gray-300"
                      />
                    </div>
                    {validationError && (
                      <p className="text-sm text-red-600" role="alert">
                        {validationError}
                      </p>
                    )}
                  </CardContent>
                  <CardFooter className="bg-white dark:bg-white flex-col gap-3">
                    <Button type="submit" disabled={isLoading} className="w-full" size="lg">
                      {isLoading ? "Updating password..." : "Set new password"}
                    </Button>
                    <Button asChild variant="ghost" className="w-full text-gray-600">
                      <Link to="/login">
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Back to sign in
                      </Link>
                    </Button>
                  </CardFooter>
                </form>
              )}
            </Card>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-white/10 backdrop-blur-sm border-t border-white/20 py-6">
          <div className="container mx-auto px-4">
            <div className="text-center">
              <p className="text-sm text-white/80">
                © {new Date().getFullYear()} BondIT Denmark ApS. All rights reserved. (
                <Version className="inline-block py-1 -my-1 text-white/80" showPrefix={false} />)
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
